import React, {useMemo} from 'react';

import {useStore} from 'effector-react';
import {TFunction} from 'i18next';
import {useTranslation} from 'react-i18next';
import {useHistory} from 'react-router-dom';

import {Page} from 'components/common/Page';
import {ButtonVariant} from 'components/ui-kit/Button';
import {Input} from 'components/ui-kit/Input';
import {ContentContainer} from 'components/ui-kit/PageComponents';
import {ROUTE} from 'core/routes';
import {permissions$} from 'models/auth';
import {filtersChanged, storagesState$} from 'models/profile/storages/table';
import {StorageAccessors as Accessors, StorageFormattedRow, StorageRawRow} from 'types/profile';
import {handleNil} from 'utils/handleNil';

import {ProfileNavigationTabs} from '../NavigationTabs';
import {Wrapper} from '../styled';

import {CreateStorageButton, HeaderControls, SearchWrapper, StorageNameButton} from './styled';
import {StoragesGridTable} from './Table';

export function convertData(
    data: StorageRawRow[] | undefined,
    t: TFunction,
    onOpenStorage: (storage: StorageRawRow) => void,
): StorageFormattedRow[] {
    if (!data) {
        return [];
    }

    return data.map((elem) => ({
        [Accessors.storageName]: handleNil(elem.storageName, (name: string) => (
            <StorageNameButton type="button" onClick={() => onOpenStorage(elem)}>
                {name}
            </StorageNameButton>
        )),
        [Accessors.address]: handleNil(elem.address),
        [Accessors.storageType]: handleNil(elem.storageTypeId, (storageTypeId: number) =>
            t(`dictionaries.storageType.${storageTypeId}.label`),
        ),
    }));
}

const getConfig = (t: TFunction) => [
    {
        id: Accessors.storageName,
        header: t('pages.profile.storages.columns.storageName'),
        filter: <Input placeholder={t('Enter value')} />,
        isSortable: true,
        width: 360,
    },
    {
        id: Accessors.address,
        header: t('pages.profile.storages.columns.address'),
        filter: <Input placeholder={t('Enter value')} />,
        isSortable: true,
        width: 420,
    },
    {
        id: Accessors.storageType,
        header: t('pages.profile.storages.columns.storageType'),
    },
];

export const Storages = () => {
    const {t} = useTranslation();
    const history = useHistory();
    const permissions = useStore(permissions$);
    const {filters} = useStore(storagesState$);
    const searchValue = useMemo(() => {
        const value = filters[Accessors.storageName];
        return typeof value === 'string' ? value : '';
    }, [filters]);

    const onOpenStorage = (storage: StorageRawRow) => {
        history.push(`${ROUTE.STORAGES}/${storage.clientId}`);
    };

    const canCreateStorage =
        permissions.includes(permissions.organizationParticipantOwnRegister) ||
        permissions.includes(permissions.participantAllRegister);

    const headerControls = (
        <HeaderControls>
            <SearchWrapper>
                <Input
                    size="sm"
                    value={searchValue}
                    placeholder={t('pages.profile.storages.search')}
                    onChange={(event) =>
                        filtersChanged([
                            {
                                id: Accessors.storageName,
                                value: event.target.value,
                            },
                        ])
                    }
                />
            </SearchWrapper>
            {canCreateStorage && (
                <CreateStorageButton
                    size="sm"
                    variant={ButtonVariant.Normal}
                    type="button"
                    onClick={() => history.push(ROUTE.STORAGES_CREATE)}
                >
                    + {t('pages.profile.storages.create')}
                </CreateStorageButton>
            )}
        </HeaderControls>
    );

    return (
        <Page title={t('pages.profile.title')}>
            <Wrapper>
                <ProfileNavigationTabs />
                <ContentContainer noPaddings>
                    <StoragesGridTable
                        configFunc={getConfig}
                        dataMapper={convertData}
                        headerControls={headerControls}
                        onOpenStorage={onOpenStorage}
                    />
                </ContentContainer>
            </Wrapper>
        </Page>
    );
};
